(() => {
  // Flip to true to trace the drawer in the console, then reproduce and copy the
  // `[td-cart-drawer]` lines.
  const DEBUG = false;
  const log = (...args) => {
    if (DEBUG) console.log('[td-cart-drawer]', ...args);
  };

  log('script evaluated', new Date().toISOString());

  const CART_URL = `${window.Shopify?.routes?.root || '/'}cart.js`;
  const POLL_INTERVAL_MS = 400;
  const MAX_POLL_ATTEMPTS = 15;

  const fetchCartSnapshot = () =>
    fetch(CART_URL, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((cart) => ({ totalDiscount: cart.total_discount, itemCount: cart.item_count }))
      .catch(() => null);

  const COUPON_ACTION_SELECTOR =
    '.docapp-coupon-input--button, .docapp-coupon-input--bean-container--bean-remove';
  const COUPON_INPUT_SELECTOR = '.docapp-coupon-input--input';

  const SUMMARY_SELECTOR = '[data-td-cart-drawer-summary]';
  const CHECKOUT_BUTTON_SELECTOR = '[data-td-cart-drawer-checkout]';

  let pollTimeout = null;
  let pollBaseline = null;
  let pollAttempts = 0;
  let drawerBusy = false;

  // While a coupon is being applied or removed both the order summary and the
  // checkout button show a loading state, since the totals on both are about
  // to change.
  const setDrawerBusy = (busy) => {
    const summaries = document.querySelectorAll(SUMMARY_SELECTOR);
    const checkoutButtons = document.querySelectorAll(CHECKOUT_BUTTON_SELECTOR);
    log('setDrawerBusy', busy, 'summaries:', summaries.length, 'checkout buttons:', checkoutButtons.length);
    drawerBusy = busy;

    summaries.forEach((summary) => {
      summary.setAttribute('aria-busy', busy ? 'true' : 'false');
    });

    checkoutButtons.forEach((button) => {
      // Never clear the state of a button that is mid checkout submission.
      if (!busy && button.dataset.tdSubmitting === 'true') return;
      button.setAttribute('aria-busy', busy ? 'true' : 'false');
    });
  };

  // Stops the polling timer only. Does NOT clear the busy indicator: when a
  // cart change is detected, the drawer section gets fully re-rendered via
  // `cart:refresh` (see theme.js CartDrawer#refreshCart), which replaces the
  // summary node entirely. Clearing busy here would briefly reveal the stale
  // (pre-discount) numbers before that swap happens, causing a visible jump.
  const stopPollTimer = () => {
    window.clearTimeout(pollTimeout);
    pollTimeout = null;
    pollAttempts = 0;
  };

  const pollForCartChange = () => {
    pollAttempts += 1;

    fetchCartSnapshot().then((snapshot) => {
      log('poll attempt', pollAttempts, 'snapshot', snapshot, 'baseline', pollBaseline);

      if (!snapshot || !pollBaseline) return;

      const changed =
        snapshot.totalDiscount !== pollBaseline.totalDiscount || snapshot.itemCount !== pollBaseline.itemCount;

      if (changed) {
        log('poll: change detected, stopping timer and dispatching cart:refresh (busy stays on)');
        stopPollTimer();
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return;
      }

      if (pollAttempts < MAX_POLL_ATTEMPTS) {
        pollTimeout = window.setTimeout(pollForCartChange, POLL_INTERVAL_MS);
      } else {
        // No change ever showed up (e.g. an invalid code was rejected) - stop
        // waiting and clear the indicator since no refresh is coming.
        log('poll: max attempts reached with no change, clearing busy');
        stopPollTimer();
        if (drawerBusy) setDrawerBusy(false);
      }
    });
  };

  // Only an actual submit action (Apply/remove click, or Enter in the
  // input) should start a fresh watch cycle. Clicking to merely focus the
  // input used to also kick off a 6s poll with nothing to actually detect,
  // and - worse - a code submitted via Enter never fired a `click` on the
  // Apply button at all, so the busy indicator silently never turned on.
  const startCouponWatch = (reason) => {
    log('coupon watch starting, reason =', reason);
    stopPollTimer();
    setDrawerBusy(true);

    fetchCartSnapshot().then((snapshot) => {
      log('coupon watch: baseline snapshot', snapshot);
      if (!snapshot) return;
      pollBaseline = snapshot;
      pollForCartChange();
    });
  };

  const handleCouponClick = (event) => {
    const mount = event.target.closest?.('[data-td-cart-drawer-coupon-mount]');
    if (!mount) return;

    const isActionTrigger = Boolean(event.target.closest?.(COUPON_ACTION_SELECTOR));
    log('coupon click inside mount, target =', event.target, 'matches action selector?', isActionTrigger);
    if (!isActionTrigger) return;

    startCouponWatch('click');
  };

  document.addEventListener('click', handleCouponClick, true);

  const handleCouponKeydown = (event) => {
    if (event.key !== 'Enter') return;

    const input = event.target.closest?.(COUPON_INPUT_SELECTOR);
    if (!input) return;

    const mount = input.closest('[data-td-cart-drawer-coupon-mount]');
    if (!mount) return;

    log('coupon Enter keydown inside mount, target =', event.target);
    startCouponWatch('enter-keydown');
  };

  document.addEventListener('keydown', handleCouponKeydown, true);

  document.addEventListener('cart-drawer:refreshed', () => {
    log('cart-drawer:refreshed fired, drawerBusy =', drawerBusy);
    if (drawerBusy) setDrawerBusy(false);
  });

  // A couple of rAF ticks is the "textbook" way to guarantee a paint before a
  // navigation, but real-world testing showed browsers can still skip
  // rendering that frame when a same-tick redirect follows (the checkout
  // POST responds with a 302 almost immediately). A deliberate delay is the
  // only reliable way to guarantee the loader is visible - and it needs to be
  // long enough for the dot-bounce animation (staggered 0/150/300ms delays,
  // ~1s per cycle) to actually complete at least one visible bounce, not
  // just flash a static frame.
  const CHECKOUT_SUBMIT_DELAY_MS = 700;

  const handleCheckoutClick = (event) => {
    log('document click, target =', event.target);

    const button = event.target.closest?.(CHECKOUT_BUTTON_SELECTOR);
    log('checkout: matched button?', button);
    if (!button) return;

    if (button.disabled || button.dataset.tdSubmitting === 'true') {
      log('checkout: bailing, disabled =', button.disabled, 'tdSubmitting =', button.dataset.tdSubmitting);
      return;
    }

    const form = button.closest('form');
    log('checkout: matched form?', form);
    if (!form) return;

    event.preventDefault();
    button.dataset.tdSubmitting = 'true';
    button.setAttribute('aria-busy', 'true');
    log('checkout: aria-busy set to true, submitting in', CHECKOUT_SUBMIT_DELAY_MS, 'ms');

    window.setTimeout(() => {
      log('checkout: timeout fired, submitting form now');
      // Submit with the button as the explicit submitter so `name="checkout"`
      // is still included in the POST body (form.submit() alone would drop it).
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(button);
      } else {
        form.submit();
      }
      button.disabled = true;
    }, CHECKOUT_SUBMIT_DELAY_MS);
  };

  document.addEventListener('click', handleCheckoutClick, true);

  const mountCouponWidget = (root = document) => {
    const drawer = root.querySelector?.('.td-cart-drawer') || (root.classList?.contains('td-cart-drawer') ? root : null);
    if (!drawer) return;

    const mount = drawer.querySelector('[data-td-cart-drawer-coupon-mount]');
    if (!mount) return;

    const couponInput = drawer.querySelector('.docapp-coupon-input') || document.querySelector('.docapp-coupon-input');
    if (!couponInput) return;

    const couponRoot = couponInput.closest('[class*="docapp"]') || couponInput.parentElement;
    if (!couponRoot || mount.contains(couponRoot)) return;

    mount.appendChild(couponRoot);
  };

  const init = (root = document) => {
    mountCouponWidget(root);
    window.setTimeout(() => mountCouponWidget(root), 250);
    window.setTimeout(() => mountCouponWidget(root), 1000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('cart-drawer:refreshed', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
